var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import classNames from 'classnames';
import { Icons } from 'src/ui/components/Icons/Icons';
import { Image } from 'src/ui/components/Image/Image';
import { ErrorPlaceholder } from 'src/ui/views/dynamics/CaseGlobalViewPage/components/FilesViewer/placeholders/ErrorPlaceholder';
import { getVideoSourceType, Video } from 'src/ui/components/VideoPlayer/components/Video/Video';
import { E_TOOLTIP_POSITION, Tooltip } from 'src/ui/components/ToolTip/Tooltip';
import { useMount, useUpdate } from 'src/shared/hooks/useLifecycle';
import { useFileViewerKeyboardListener } from 'src/ui/views/dynamics/CaseGlobalViewPage/components/FullScreenFilesViewer/fileViewerKeyboardListener';
import { calculatePositionByScaleAndMousePosition, getTransformProperties, setTransformStyle, } from 'src/ui/views/dynamics/CaseGlobalViewPage/components/FullScreenFilesViewer/helpers';
import { COLORS } from 'src/shared/styles/colors';
import { E_ICONS } from 'src/ui/components/Icons/constants';
import styles from './FullScreenFilesViewer.module.scss';
import { FULLSCREEN_FILE_VIEWER_MAX_ZOOM, FULLSCREEN_FILE_VIEWER_MIN_ZOOM, } from 'src/ui/views/dynamics/CaseGlobalViewPage/components/FullScreenFilesViewer/constants';
import { getPropsByKeyPath } from 'src/shared/helpers/objHelpers';
import { rotateBase64 } from 'src/shared/helpers/viewsHelpers';
export var FullScreenFilesViewer = function (props) {
    var _a;
    var i18n = props.i18n, currentDocument = props.currentDocument, currentPage = props.currentPage, sources = props.sources, onPageChange = props.onPageChange, onClose = props.onClose, onSourcesChange = props.onSourcesChange;
    var _b = useState(null), dynamicStyle = _b[0], setDynamicStyle = _b[1];
    var _c = useState(FULLSCREEN_FILE_VIEWER_MIN_ZOOM), currentScale = _c[0], setCurrentScale = _c[1];
    var _d = useState(null), draggingPos = _d[0], setDraggingPos = _d[1];
    var _e = useState(false), isScrolling = _e[0], setIsScrolling = _e[1];
    var containerRef = useRef(null);
    var timeoutEndScroll = null;
    var documentViewerErrorMessage = getPropsByKeyPath(i18n === null || i18n === void 0 ? void 0 : i18n.modules, 'documentViewer.errorMessage');
    useUpdate(function () {
        setCurrentScale(FULLSCREEN_FILE_VIEWER_MIN_ZOOM);
        setDynamicStyle(null);
    }, [currentPage]);
    // we don't want to run this effect when currentPage change, it's only run when heightOfContainer change on Mount
    useMount(function () {
        if (containerRef.current) {
            var contentHeight = containerRef.current.offsetHeight + 16; // 16 is for row gap
            containerRef.current.scrollTo(0, currentPage * contentHeight);
        }
    });
    var handleOnWheel = function (e) {
        var ctrlPressed = e.ctrlKey;
        var wheelIntensity = e.nativeEvent.wheelDelta;
        if (ctrlPressed && !isScrolling) {
            containerRef.current.style.overflow = 'hidden';
            var directionToTop = wheelIntensity > 0;
            var zoomIntensity = 0.1;
            var newScale = directionToTop
                ? Math.min(FULLSCREEN_FILE_VIEWER_MAX_ZOOM, currentScale + zoomIntensity)
                : Math.max(FULLSCREEN_FILE_VIEWER_MIN_ZOOM, currentScale - zoomIntensity);
            var scaleCondition = directionToTop
                ? newScale <= FULLSCREEN_FILE_VIEWER_MAX_ZOOM
                : newScale >= FULLSCREEN_FILE_VIEWER_MIN_ZOOM;
            if (scaleCondition && newScale !== currentScale) {
                var _a = calculatePositionByScaleAndMousePosition(e, newScale), scale_1 = _a.scale, position_1 = _a.position;
                setCurrentScale(scale_1);
                setDynamicStyle(function (prevState) { return (__assign(__assign({}, prevState), { transform: setTransformStyle(scale_1, position_1) })); });
            }
        }
        else {
            containerRef.current.style.overflow = 'auto';
        }
    };
    var handleFilesEndScroll = function (e) {
        clearTimeout(timeoutEndScroll);
        if (containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) {
            e.preventDefault();
            if (!isScrolling) {
                setIsScrolling(function () { return true; });
            }
            // To trigger end of scrolling no react event exists, so we need to use setTimeout
            timeoutEndScroll = setTimeout(function () {
                var _a = e.target, scrollTop = _a.scrollTop, clientHeight = _a.clientHeight;
                var newPage = Math.floor(scrollTop / clientHeight);
                onPageChange(newPage);
                if (isScrolling) {
                    setIsScrolling(function () { return false; });
                }
            }, 500); // 500ms to prevent too much callback on scroll event
        }
    };
    var handleDoubleClickOnPage = function (e) {
        if (currentScale > FULLSCREEN_FILE_VIEWER_MIN_ZOOM) {
            setCurrentScale(FULLSCREEN_FILE_VIEWER_MIN_ZOOM);
            setDynamicStyle(null);
        }
        else {
            var _a = calculatePositionByScaleAndMousePosition(e, FULLSCREEN_FILE_VIEWER_MIN_ZOOM + 1), scale_2 = _a.scale, position_2 = _a.position;
            setCurrentScale(scale_2);
            setDynamicStyle(function (prevState) { return (__assign(__assign({}, prevState), { transform: setTransformStyle(scale_2, position_2) })); });
        }
    };
    var handleStartDragging = function (e) {
        // Firefox have its own way to handle Mousedown event, so we need to prevent this default behavior
        e.preventDefault();
        return currentScale > FULLSCREEN_FILE_VIEWER_MIN_ZOOM
            ? setDraggingPos({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY })
            : null;
    };
    var handleEndDragging = function (e) { return setDraggingPos(null); };
    var handleDragging = function (e) {
        e.preventDefault();
        // handle dragging only when scale > 1
        if (draggingPos && currentScale > FULLSCREEN_FILE_VIEWER_MIN_ZOOM && dynamicStyle) {
            // get current position of image
            var position = getTransformProperties(dynamicStyle.transform).position;
            var xDifference = draggingPos.x - e.nativeEvent.offsetX;
            var yDifference = draggingPos.y - e.nativeEvent.offsetY;
            var aliasing = 5;
            // update only when difference is bigger than aliasing, improve performance
            if (xDifference > aliasing || xDifference < -aliasing || yDifference > aliasing || yDifference < -aliasing) {
                // calculate new position of image with mouse position before dragging and mouse position after dragging
                var offsetX_1 = position.x - xDifference;
                var offsetY_1 = position.y - yDifference;
                // must update drag position for next dragging event
                setDraggingPos({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
                setDynamicStyle(function (prevStyle) { return (__assign(__assign({}, prevStyle), { transform: setTransformStyle(currentScale, { x: offsetX_1, y: offsetY_1 }) })); });
            }
        }
    };
    var handleDecreaseScale = function () {
        var currentTransform = getTransformProperties(dynamicStyle === null || dynamicStyle === void 0 ? void 0 : dynamicStyle.transform);
        if (!zoomOutDisabled) {
            var minScale_1 = Math.max(FULLSCREEN_FILE_VIEWER_MIN_ZOOM, currentScale - 1);
            var shouldResetPosition_1 = minScale_1 === FULLSCREEN_FILE_VIEWER_MIN_ZOOM;
            setCurrentScale(minScale_1);
            setDynamicStyle(function (prevStyle) { return (__assign(__assign({}, prevStyle), { transform: setTransformStyle(minScale_1, shouldResetPosition_1 ? { x: 0, y: 0 } : currentTransform.position) })); });
        }
        else {
            return null;
        }
    };
    var handleIncreaseScale = function () {
        var currentTransform = getTransformProperties(dynamicStyle === null || dynamicStyle === void 0 ? void 0 : dynamicStyle.transform);
        if (zoomOutDisabled) {
            var firstZoom_1 = FULLSCREEN_FILE_VIEWER_MIN_ZOOM + 1;
            setCurrentScale(firstZoom_1);
            setDynamicStyle(function (prevStyle) { return (__assign(__assign({}, prevStyle), { transform: setTransformStyle(firstZoom_1, currentTransform.position) })); });
        }
        else if (currentScale < FULLSCREEN_FILE_VIEWER_MAX_ZOOM) {
            var maxScale_1 = Math.min(FULLSCREEN_FILE_VIEWER_MAX_ZOOM, currentScale + 1);
            setCurrentScale(maxScale_1);
            setDynamicStyle(function (prevStyle) { return (__assign(__assign({}, prevStyle), { transform: setTransformStyle(maxScale_1, currentTransform.position) })); });
        }
    };
    var handleRotateRight = function () {
        var imageToUpdate = sources[currentPage];
        rotateBase64(imageToUpdate.src, true).then(function (base64) {
            var sourceRotated = __assign(__assign({}, imageToUpdate), { src: base64 || '' });
            onSourcesChange(sourceRotated);
        });
    };
    var handleRotateLeft = function () {
        var imageToUpdate = sources[currentPage];
        rotateBase64(imageToUpdate.src, false).then(function (base64) {
            var sourceRotated = __assign(__assign({}, imageToUpdate), { src: base64 || '' });
            onSourcesChange(sourceRotated);
        });
    };
    var handleOnClose = function () {
        // if dragging, don't close
        return !draggingPos ? onClose() : setDraggingPos(null);
    };
    useFileViewerKeyboardListener(currentScale, handleIncreaseScale, handleDecreaseScale, handleRotateRight, handleRotateLeft);
    var currentTitle = (_a = sources[currentPage]) === null || _a === void 0 ? void 0 : _a.name;
    var zoomOutDisabled = currentScale <= FULLSCREEN_FILE_VIEWER_MIN_ZOOM;
    var zoomInDisabled = currentScale >= FULLSCREEN_FILE_VIEWER_MAX_ZOOM;
    var rotateLeftTooltip = getPropsByKeyPath(i18n.components, 'tooltips.fullscreenViewer.rotateLeft');
    var rotateRightTooltip = getPropsByKeyPath(i18n.components, 'tooltips.fullscreenViewer.rotateRight');
    var videoZoomInTooltip = getPropsByKeyPath(i18n.components, 'tooltips.video.zoomIn');
    var videoZoomOutTooltip = getPropsByKeyPath(i18n.components, 'tooltips.video.zoomOut');
    var zoomInTooltip = getPropsByKeyPath(i18n.components, 'tooltips.fullscreenViewer.zoomIn');
    var zoomOutTooltip = getPropsByKeyPath(i18n.components, 'tooltips.fullscreenViewer.zoomOut');
    return (_jsxs("div", __assign({ className: styles.fullScreenFilesViewerWrapper, onClick: handleOnClose }, { children: [_jsxs("div", __assign({ className: styles.fullScreenFilesViewerHeader, onClick: function (e) { return e.stopPropagation(); } }, { children: [_jsx("div", __assign({ className: styles.fullScreenFilesViewerHeaderTitle }, { children: currentTitle })), _jsxs("div", __assign({ className: styles.fullScreenFilesViewerHeaderActions }, { children: [_jsxs("span", { children: [_jsx(Tooltip, __assign({ position: E_TOOLTIP_POSITION.BOTTOM }, { children: rotateLeftTooltip })), _jsx(Icons, { icon: E_ICONS.ROTATE_LEFT, onClick: handleRotateLeft, color: COLORS.WHITE })] }), _jsxs("span", { children: [_jsx(Tooltip, __assign({ position: E_TOOLTIP_POSITION.BOTTOM }, { children: rotateRightTooltip })), _jsx(Icons, { icon: E_ICONS.ROTATE_RIGHT, onClick: handleRotateRight, color: COLORS.WHITE })] }), _jsxs("span", { children: [_jsx(Tooltip, __assign({ position: E_TOOLTIP_POSITION.BOTTOM }, { children: zoomOutTooltip })), _jsx(Icons, { icon: E_ICONS.ZOOM_OUT, onClick: handleDecreaseScale, color: COLORS.WHITE, disabled: zoomOutDisabled })] }), _jsxs("span", { children: [_jsx(Tooltip, __assign({ position: E_TOOLTIP_POSITION.BOTTOM }, { children: zoomInTooltip })), _jsx(Icons, { icon: E_ICONS.ZOOM_IN, onClick: handleIncreaseScale, color: COLORS.WHITE, disabled: zoomInDisabled })] }), _jsx(Icons, { icon: E_ICONS.CLOSE, onClick: onClose, color: COLORS.RED['500'], className: styles.fullScreenFilesViewerCloseIcon })] }))] })), _jsx("div", __assign({ className: styles.fullScreenFilesViewerContentWrapper }, { children: _jsx("div", __assign({ ref: containerRef, className: styles.fullScreenFilesViewerContent, style: { gridTemplateRows: "repeat(".concat((currentDocument === null || currentDocument === void 0 ? void 0 : currentDocument.files.length) || 0, ", 100%)") }, onScroll: handleFilesEndScroll, onWheel: handleOnWheel }, { children: sources.map(function (source, index) {
                        var transformStyle = getTransformProperties(dynamicStyle === null || dynamicStyle === void 0 ? void 0 : dynamicStyle.transform);
                        return (_jsx("div", __assign({ className: styles.fullScreenPageWrapper }, { children: _jsx("div", __assign({ className: styles.fullScreenPage, style: index === currentPage ? { transform: dynamicStyle === null || dynamicStyle === void 0 ? void 0 : dynamicStyle.transform } : null, onClick: function (e) { return e.stopPropagation(); }, onDoubleClick: handleDoubleClickOnPage, onMouseDown: handleStartDragging, onMouseMove: handleDragging, onMouseUp: handleEndDragging }, { children: source.type === 'image' ? (_jsx(Image, { isLoading: source.isLoading, src: source.isError ? '' : source.src, alt: 'file', className: classNames(styles.image, transformStyle.scale > 1 ? styles.isScaled : null), placeholderOnError: _jsx(ErrorPlaceholder, { title: documentViewerErrorMessage, text: source.name }) })) : (_jsx(Video, { sources: [
                                        { src: source.src, type: source.mimeType || getVideoSourceType(source.src) },
                                    ], withTimeValidation: false, zoomInTooltip: videoZoomInTooltip, zoomOutTooltip: videoZoomOutTooltip })) }), "".concat(source.src).concat(index)) })));
                    }) })) })), _jsx("div", __assign({ className: styles.fullScreenFilesViewerPageIndicator }, { children: "".concat(currentPage + 1, " / ").concat(sources.length) }))] })));
};
